import { Box, Button, /*Button,*/ Typography } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import LinearProgress from "@material-ui/core/LinearProgress";
import { withStyles } from "@material-ui/core/styles";
import { Create, RadioButtonChecked } from "@material-ui/icons";
import DragHandle from "@material-ui/icons/DragHandle";
import classNames from "classnames";
import update from "immutability-helper";
import range from "lodash/range";
import React from "react";
import { Prompt } from "react-router-dom";
import { SortEnd, SortableHandle, arrayMove } from "react-sortable-hoc";
import Component from "src/components/Component";
import Page from "src/components/Page";
import PartialCleanCheck from "src/components/PartialCleanCheck";
import RadioScale from "src/components/RadioScale";
import SortableList from "src/components/SortableList";
import withAxios from "src/components/withAxios";
import {
  ITemplate,
  ITemplateData,
  ITemplateDirectorRequest,
  ITemplateQuestion,
  TemplateQuestionCategory,
  TemplateQuestionType,
  TemplateQuestionUtil,
} from "src/services";
import { SaveButton, TemplateSettings } from "./components";
import {
  ATHLETE_COLUMN_HEADING,
  COACH_COLUMN_HEADING,
  EXPECTATIONS_QUESTIONS_HEADING,
  GET_TEMPLATE_PAGE_SUBTITLE,
  PARENT_QUESTIONS_HEADING,
  PRINT_BUTTON_LABEL,
  // PRINT_BUTTON_LABEL,
  TEMPLATE_PAGE_HEADER,
  UNSAVED_CHANGES_PROMPT,
  WARMTH_QUESTIONS_HEADING,
  WRITTEN_QUESTIONS_HEADING,
} from "./constants";
import { styles } from "./styles";
import { IProps, ITemplateEditorState, ITemplateQuestionEdit } from "./types";
import { ClassNameMap } from "@material-ui/core/styles/withStyles";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
// import SurveyPrinter, {
//   PrintSurveyFunc,
// } from "src/pages/director/survey/SurveyPrinter";

export const noneIdValue = "none";

const AthleteCoachHeading = ({
  classes,
  isEditorMode,
}: {
  classes: ClassNameMap<string>;
  isEditorMode: boolean;
}) => (
  <Box
    className={classes.columnHeadingsWrapper}
    marginLeft={!isEditorMode ? "3rem" : "0"}
  >
    <Typography variant="h6">{ATHLETE_COLUMN_HEADING}</Typography>
    <Typography variant="h6">{COACH_COLUMN_HEADING}</Typography>
  </Box>
);

export default withStyles(styles)(
  withAxios(
    class TemplateEditorPage extends Component<IProps, ITemplateEditorState> {
      private handleNameChange = this.stateInputEventHandler((v) => ({
        name: v,
      }));

      private handleDefaultPositiveTextChange = this.stateTextAreaEventHandler(
        (v) => ({ defaultPositiveText: v })
      );

      private handleDefaultNegativeTextChange = this.stateTextAreaEventHandler(
        (v) => ({ defaultNegativeText: v })
      );

      // private printEvaluation?: PrintSurveyFunc;

      private partialCleanCheck: PartialCleanCheck<ITemplateEditorState> =
        new PartialCleanCheck(this, [
          "name",
          "parentId",
          "questions",
          "defaultPositiveText",
          "defaultNegativeText",
          "defaultReversed",
          "defaultScale",
          "enableParentQuestions",
          "templateType",
        ]);

      private DragHandle = SortableHandle(() => <DragHandle />);
      private questionLocalId = 0; // Keeps track of how many questions we have so that we always have a unique id

      constructor(props: IProps) {
        super(props);
        this.state = {
          copyId: null,
          defaultNegativeText: "Strongly\nDisagree",
          defaultPositiveText: `Strongly\nAgree`,
          defaultReversed: false,
          defaultScale: 7,
          enableParentQuestions: false,
          name: "",
          parentId: null,
          parentQuestions: [],
          questions: [],
          scalePreview: "",
          showPrintButton: false,
          showScaleOptions: false,
          templateType: 0,
          templates: [],
        };
        this.partialCleanCheck.setOldFormData(this.state);
      }

      public componentDidMount() {
        this.loadTemplate(this.props.templateId);
        this.loadInheritTemplates();
      }
      public generatePDF = () => {
        const input: any = document.getElementById('pdfContent');
        const margin = 10; // Define your margin in mm
    
        html2canvas(input, { scale: 2 }).then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
    
            const pdfWidth = 210; // A4 size width in mm
            const pdfHeight = 297; // A4 size height in mm
            const imgWidth = pdfWidth - 2 * margin; // Subtract the margins from width
            const imgHeight = (canvas.height * imgWidth) / canvas.width; // Adjust height proportionally
    
            let heightLeft = imgHeight;
            let position = margin; // Start at the margin
    
            // Add the first image with margin
            pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
            heightLeft -= (pdfHeight - 2 * margin); // Adjust for margins
    
            // Create pages if the content exceeds one page, without repeating
            while (heightLeft > 0) {
                position = -(imgHeight - heightLeft) // Adjust position for the remaining content
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
                heightLeft -= (pdfHeight - 2 * margin);
            }
    
            // Save the PDF or open in new tab
            const pdfOutput = pdf.output('blob');
            const pdfURL = URL.createObjectURL(pdfOutput);
            window.open(pdfURL);
        });
    };
    

      public render() {
        const { classes, isEditorMode, templateId } = this.props;
        const {
          name,
          loading,
          parentId,
          copyId,
          defaultPositiveText,
          defaultNegativeText,
          defaultReversed,
          defaultScale,
          enableParentQuestions,
          parentQuestions,
          postingTemplate,
          questions,
          scalePreview,
          submitting,
          templates,
          templateType,
        } = this.state;
        const parentTemplate = !(
          templateId === undefined ||
          templates.findIndex(
            (template) => template.parentId === templateId
          ) === -1
        );

        const questionsToRender = (
          isEditorMode ? questions : parentQuestions
        ) as ITemplateQuestionEdit[];

        const expectationsQuestions = this.getQuestionCategory(
          TemplateQuestionCategory.expectations,
          questionsToRender
        );
        const warmthQuestions = this.getQuestionCategory(
          TemplateQuestionCategory.warmth,
          questionsToRender
        );
        const otherQuestions = this.getQuestionCategory(
          TemplateQuestionCategory.other,
          questionsToRender
        );
        const parentCategoryQuestions = this.getQuestionCategory(
          TemplateQuestionCategory.parent,
          questionsToRender
        );

        const hasExpectationsQuestions = expectationsQuestions.length > 0;
        const hasWarmthQuestions = warmthQuestions.length > 0;
        const hasOtherQuestions = otherQuestions.length > 0;
        const hasParentQuestions = parentCategoryQuestions.length > 0;

        return (
          <Page
            header={TEMPLATE_PAGE_HEADER}
            subtitle={GET_TEMPLATE_PAGE_SUBTITLE(name)}
          >
            <Prompt
              when={
                !this.partialCleanCheck.isClean() &&
                postingTemplate === undefined
              }
              message={UNSAVED_CHANGES_PROMPT}
            />
            {(!!loading || !!submitting) && <LinearProgress />}
            {/* {!isEditorMode && (
              <>
                <SurveyPrinter printSurveyRef={this.setPrintSurvey} />
                <Box marginBottom="1.5rem">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={this.handlePrintClick}
                  >
                    {PRINT_BUTTON_LABEL}
                  </Button>
                </Box>
              </>
            )} */}
            {!isEditorMode && <Button
              variant="contained"
              color="primary"
              onClick={this.generatePDF}
            >
              {PRINT_BUTTON_LABEL}
            </Button>}
            <div id="pdfContent">
              <Box display="flex" flexDirection="column" gridGap="2.5rem">
                {isEditorMode && (
                  <TemplateSettings
                    SaveButtonWithProps={() => (
                      <SaveButton
                        templateId={templateId}
                        submitTemplate={this.submitTemplate}
                        updateTemplate={this.updateTemplate}
                        canSubmit={this.canSubmit}
                        isClean={this.partialCleanCheck.isClean}
                      />
                    )}
                    name={name}
                    templates={templates}
                    templateId={templateId}
                    parentId={parentId}
                    copyId={copyId}
                    defaultPositiveText={defaultPositiveText}
                    defaultNegativeText={defaultNegativeText}
                    defaultReversed={defaultReversed}
                    defaultScale={defaultScale}
                    enableParentQuestions={enableParentQuestions}
                    scalePreview={scalePreview}
                    templateType={templateType}
                    parentTemplate={parentTemplate}
                    handleParentTemplateChange={this.handleParentTemplateChange}
                    handleTemplateTypeChange={this.handleTemplateTypeChange}
                    handleCopyTemplateChange={this.handleCopyTemplateChange}
                    handleEnableParentQuestions={this.handleEnableParentQuestions}
                    handleReversedChange={this.handleReversedChange}
                    handleScaleChange={this.handleScaleChange}
                    loadCopyQuestions={this.loadCopyQuestions}
                    handleNameChange={this.handleNameChange}
                    handleDefaultPositiveTextChange={
                      this.handleDefaultPositiveTextChange
                    }
                    handleDefaultNegativeTextChange={
                      this.handleDefaultNegativeTextChange
                    }
                    handleScalePreviewChange={this.handleScalePreviewChange}
                  />
                )}
                {(hasExpectationsQuestions || isEditorMode) && (
                  <Box>
                    <Typography
                      className={classes.questionCategoryHeading}
                      variant="h5"
                    >
                      {EXPECTATIONS_QUESTIONS_HEADING}
                    </Typography>
                    {hasExpectationsQuestions && (
                      <AthleteCoachHeading
                        classes={classes}
                        isEditorMode={isEditorMode}
                      />
                    )}
                    {this.renderQuestions(
                      expectationsQuestions,
                      TemplateQuestionCategory.expectations,
                      false
                    )}
                  </Box>
                )}
                {(hasWarmthQuestions || isEditorMode) && (
                  <Box>
                    <Typography
                      className={classes.questionCategoryHeading}
                      variant="h5"
                    >
                      {WARMTH_QUESTIONS_HEADING}
                    </Typography>
                    {hasWarmthQuestions && (
                      <AthleteCoachHeading
                        classes={classes}
                        isEditorMode={isEditorMode}
                      />
                    )}
                    {this.renderQuestions(
                      warmthQuestions,
                      TemplateQuestionCategory.warmth,
                      false
                    )}
                  </Box>
                )}
                {(hasOtherQuestions || isEditorMode) && (
                  <Box>
                    <Typography
                      className={classes.questionCategoryHeading}
                      variant="h5"
                    >
                      {WRITTEN_QUESTIONS_HEADING}
                    </Typography>
                    {hasOtherQuestions && (
                      <AthleteCoachHeading
                        classes={classes}
                        isEditorMode={isEditorMode}
                      />
                    )}
                    {this.renderQuestions(
                      otherQuestions,
                      TemplateQuestionCategory.other,
                      false
                    )}
                  </Box>
                )}
                {(hasParentQuestions || isEditorMode) && (
                  <Box>
                    <Typography
                      className={classes.questionCategoryHeading}
                      variant="h5"
                    >
                      {PARENT_QUESTIONS_HEADING}
                    </Typography>
                    {this.renderQuestions(
                      parentCategoryQuestions,
                      TemplateQuestionCategory.parent,
                      true
                    )}
                  </Box>
                )}
              </Box>
            </div>
          </Page>
        );
      }

      // private readonly handlePrintClick = () => {
      //   if (this.printEvaluation === undefined) {
      //     throw new Error("There is not print");
      //   }
      //   const { parentId } = this.state;
      //   this.printEvaluation(parentId ?? NaN);
      // };

      // private readonly setPrintSurvey = (
      //   printEvaluation: PrintSurveyFunc | null
      // ) => {
      //   this.printEvaluation = printEvaluation ?? undefined;
      //   this.setState({ showPrintButton: !!this.printEvaluation });
      // };

      private handleParentTemplateChange = async (
        e: React.ChangeEvent<HTMLSelectElement>
      ) => {
        const parentId =
          e.target.value === noneIdValue ? null : parseInt(e.target.value, 10);
        this.loadParentQuestions(parentId);
        this.setState({ parentId });
      };

      private handleTemplateTypeChange = async (
        e: React.ChangeEvent<HTMLSelectElement>
      ) => {
        const templateType = Number(e.target.value);
        this.setState({ templateType });
      };

      private handleCopyTemplateChange = async (
        e: React.ChangeEvent<HTMLSelectElement>
      ) => {
        const copyId =
          e.target.value === noneIdValue ? null : parseInt(e.target.value, 10);
        this.setState({ copyId });
      };

      private handleEnableParentQuestions = async (
        e: React.ChangeEvent<HTMLInputElement>
      ) => this.setState({ enableParentQuestions: e.target.checked });

      private handleReversedChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ defaultReversed: e.target.checked });

      private handleScaleChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
        this.setState({ defaultScale: parseInt(e.target.value, 10) });

      private loadCopyQuestions = () =>
        void this.runCancellableStatefulAsync(
          this,
          {
            loading: true,
          },
          {
            loading: undefined,
          },
          async () => {
            const { copyId } = this.state;
            if (copyId !== null) {
              const { questions } = await this.buildGet<ITemplate>(
                this,
                `template/${copyId}`
              )();
              this.setState((prevState) => {
                const newState = {
                  questions: prevState.questions.concat(
                    questions
                      .filter((q) => q.templateId === copyId)
                      .map((q) => ({
                        ...q,
                        deleted: undefined,
                        id: -1,
                        localId: this.questionLocalId++,
                        templateId: -1,
                      }))
                  ),
                };
                this.partialCleanCheck.setOldFormData(newState);
                return newState;
              });
            }
          },
          () => {
            this.setState({ loading: undefined });
          }
        );

      private loadParentQuestions = (templateId: number | null) =>
        void this.runCancellableStatefulAsync(
          this,
          {
            loading: true,
          },
          {
            loading: undefined,
          },
          async () => {
            if (templateId === null) {
              // If we don't have an parent id set, don't make the request
              this.setState({ parentQuestions: [] });
            } else {
              this.setState({
                parentQuestions: (
                  await this.buildGet<ITemplate>(
                    this,
                    `template/${templateId}`
                  )()
                ).questions,
              });
            }
          },
          () => {
            this.setState({ loading: undefined });
          }
        );

      private loadInheritTemplates = () =>
        void this.runCancellableStatefulAsync(
          this,
          {
            loading: true,
          },
          {
            loading: undefined,
          },
          async () => {
            this.setState({
              templates: await this.buildGet<ITemplate[]>(this, "template")(),
            });
          },
          () => {
            this.setState({ loading: undefined });
          }
        );

    private loadTemplate = (templateId: number | undefined) =>
  void this.runCancellableStatefulAsync(
    this,
 {
            loading: true,
          },
          {
            loading: undefined,
          },
    async () => {
      if (templateId !== undefined) {
        const response = await this.buildGet<ITemplate>(
          this,
          `template/${templateId}`
        )();

        console.log("Template API Response:", response);
        const {
          defaultNegativeText,
          defaultPositiveText,
          defaultReversed,
          defaultScale,
          enableParentQuestions,
          parentId,
          name,
          questions,
          type,
        } = response;
        this.setState((prevState) => {
          const newState = {
            defaultNegativeText,
            defaultPositiveText,
            defaultReversed,
            defaultScale,
            enableParentQuestions,
            name,
            parentId,
            // Questions also include inherited questions, and to keep things simple, just filter them out and load them again seperately
            questions: parentId === null ? 
            questions
              .filter((q) => q.templateId === templateId)
              .map((q) => ({
                ...q,
                deleted: undefined,
                localId: this.questionLocalId++,
              })) : questions
              .filter((q) => q.templateId !== templateId)
              .map((q) => ({
                ...q,
                deleted: undefined,
                localId: this.questionLocalId++,
              }))
              ,
            templateType: type,
          };
          this.partialCleanCheck.setOldFormData(newState);
          return newState;
        });
        this.loadParentQuestions(parentId);
      }
    },
    () => {
      this.setState({ loading: undefined });
    }
  );

      private submitTemplate = () =>
        void this.runCancellableStatefulAsync(
          this,
          {
            postingTemplate: true,
            submitting: true,
          },
          {
            postingTemplate: undefined,
            submitting: undefined,
          },
          async () => {
            const {
              parentId,
              name,
              questions,
              defaultNegativeText,
              defaultPositiveText,
              defaultReversed,
              defaultScale,
              enableParentQuestions,
              templateType,
            } = this.state;
            await this.buildPost<ITemplateData, void>(
              this,
              "template"
            )({
              defaultNegativeText,
              defaultPositiveText,
              defaultReversed,
              defaultScale,
              enableParentQuestions,
              name,
              parentId,
              questions,
              type: templateType,
            });
            await this.props.history.push(`/admin/templates`);
            await this.loadTemplate(this.props.templateId);
          },
          () => {
            this.setState({
              postingTemplate: undefined,
              submitting: undefined,
            });
          }
        );

      private updateTemplate = () =>
        void this.runCancellableStatefulAsync(
          this,
          {
            submitting: true,
          },
          {
            submitting: undefined,
          },
          async () => {
            const { templateId, schoolId } = this.props;
            const {
              parentId,
              name,
              questions,
              defaultPositiveText,
              defaultNegativeText,
              defaultReversed,
              defaultScale,
              enableParentQuestions,
              templateType,
            } = this.state;
            if (schoolId === undefined) {
              // No school Id means we are in the admin view
              await this.buildPut<ITemplateData, void>(
                this,
                `admin/template/${templateId}`
              )({
                defaultNegativeText,
                defaultPositiveText,
                defaultReversed,
                defaultScale,
                enableParentQuestions,
                name,
                // idk how parentId ever gets set to a string. It happends when the template is loaded from the server, but everything looks like type of number
                // the parentId was a bigint in the database
                parentId,
                // Filter out local questions that have been deleted
                questions: questions.filter(
                  (q) => !(q.id === -1 && q.deleted === true)
                ),
                type: templateType,
              });
            } else {
              // School Id means we are in the director view
              await this.buildPut<ITemplateDirectorRequest, void>(
                this,
                `template/${templateId}`
              )({
                questions: questions.filter(
                  (q) => !(q.id === -1 && q.deleted === true)
                ),
                schoolId,
              });
            }
            await this.loadTemplate(this.props.templateId);
          },
          () => {
            this.setState({ submitting: undefined });
          }
        );

      private handleScalePreviewChange = (
        e: React.ChangeEvent<{}>,
        value: string
      ) =>
        this.setState({
          scalePreview: value,
        });

      private handleQuestionChangeTextArea =
        (question: ITemplateQuestionEdit, field: string) =>
          (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const value = e.target.value;
            this.setState((prevState) =>
              update(prevState, {
                questions: {
                  [prevState.questions.indexOf(question)]: {
                    [field]: { $set: value },
                  },
                },
              })
            );
          };

      private handleQuestionChangeInputElement =
        (question: ITemplateQuestionEdit, field: string) =>
          (e: React.ChangeEvent<HTMLInputElement>) => {
            const value =
              e.target.type === "checkbox" ? e.target.checked : e.target.value;
            this.setState((prevState) =>
              update(prevState, {
                questions: {
                  [prevState.questions.indexOf(question)]: {
                    [field]: { $set: value },
                  },
                },
              })
            );
          };

      private handleQuestionChangeSelectElement =
        (question: ITemplateQuestionEdit, field: string) =>
          (e: React.ChangeEvent<HTMLSelectElement>) => {
            const value = e.target.value;
            this.setState((prevState) =>
              update(prevState, {
                questions: {
                  [prevState.questions.indexOf(question)]: {
                    [field]: { $set: parseInt(value, 10) },
                  },
                },
              })
            );
          };

      private handleQuestionTypeChange =
        (question: ITemplateQuestionEdit) =>
          (e: React.ChangeEvent<HTMLSelectElement>) => {
            const value = e.target.value;
            this.setState((prevState) =>
              update(prevState, {
                questions: {
                  [prevState.questions.indexOf(question)]: {
                    type: { $set: parseInt(value, 10) },
                  },
                },
              })
            );
          };

      private setToDefault =
        (question: ITemplateQuestionEdit) =>
          (e: React.MouseEvent<HTMLButtonElement>) => {
            const {
              defaultNegativeText,
              defaultPositiveText,
              defaultReversed,
              defaultScale,
            } = this.state;
            this.setState((prevState) =>
              update(prevState, {
                questions: {
                  [prevState.questions.indexOf(question)]: {
                    scaleNegativeText: { $set: defaultNegativeText },
                    scalePositiveText: { $set: defaultPositiveText },
                    scaleReversed: { $set: defaultReversed },
                    scaleScale: { $set: defaultScale },
                  },
                },
              })
            );
          };

      private handleNewQuestion =
        (category: TemplateQuestionCategory) => () => {
          const {
            defaultNegativeText,
            defaultPositiveText,
            defaultScale,
            defaultReversed,
          } = this.state;
          const questionCategory = this.getQuestionCategory(category);
          const order = (() => {
            const lastQuestion = questionCategory[questionCategory.length - 1];
            if (lastQuestion !== undefined) {
              return lastQuestion.order + 1;
            }
            return 0;
          })();
          const question: ITemplateQuestionEdit = {
            category,
            coachText: "",
            id: -1,
            localId: this.questionLocalId++,
            order,
            parentText: "",
            scaleNegativeText: defaultNegativeText,
            scalePositiveText: defaultPositiveText,
            scaleReversed: defaultReversed,
            scaleScale: defaultScale,
            studentText: "",
            templateId: -1,
            type: TemplateQuestionType.scale,
            status: false,
          };
          this.setState((prevState) =>
            update(prevState, {
              questions: { $push: [question] },
            })
          );
        };

      private handleQuestionDeleteToggle =
        (question: ITemplateQuestionEdit) => () => {
          // We need to update the order on all the questions after the deleted question
          const deleted = (() => {
            const d =
              question.deleted === undefined || question.deleted === false;
            return d ? true : undefined;
          })();
          // TODO Update the order property of all those behind the question
          // Mark the question as deleted
          this.setState((prevState) =>
            update(prevState, {
              questions: {
                [prevState.questions.indexOf(question)]: {
                  deleted: { $set: deleted },
                },
              },
            })
          );
        };

      private handleQuestionLockToggle =
  (question: ITemplateQuestionEdit) => () => {
    this.setState((prevState) =>
      update(prevState, {
        questions: {
          [prevState.questions.indexOf(question)]: {
            status: { $set: !(question.status ?? false) },
          },
        },
      })
    );
  };

      private toggleShowScaleOptions =
        () => (e: React.MouseEvent<HTMLElement>) => {
          this.setState((oldState) => ({
            questions: oldState.questions.map((question) => ({ ...question })),
            showScaleOptions: !oldState.showScaleOptions,
          }));
        };

      private handleOrderChange =
        (category: TemplateQuestionCategory) => (sort: SortEnd) => {
          // find the index of the category
          const categoryIndex = this.state.questions.findIndex(
            (q) => q.category === category
          );
          // Re-order the current question category
          this.setState({
            questions: arrayMove(
              this.state.questions,
              categoryIndex + sort.oldIndex,
              categoryIndex + sort.newIndex
            ).map((q, i) => {
              return Object.assign(q, { order: i });
            }),
          });
        };

      private renderQuestions(
        questions: ITemplateQuestionEdit[],
        category: TemplateQuestionCategory,
        parentSection: boolean
      ) {
        const { isEditorMode } = this.props;

        return (
          <Box>
            {isEditorMode && (
              <>
                <SortableList
                  renderItem={this.renderQuestion}
                  items={this.getQuestionCategory(category)}
                  onOrderChange={this.handleOrderChange(category)}
                />
                <Box marginTop="1rem">{this.renderNewQuestion(category)}</Box>
              </>
            )}
            {!isEditorMode && (
              <Box
                display="flex"
                flexDirection="column"
                gridGap="0.25rem"
                borderRadius="0.5rem"
                overflow="hidden"
              >
                {questions.map((question) => (
                  <Box key={question.id}>
                    {this.renderInheritQuestion(question, parentSection)}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );
      }

      private renderNewQuestion(category: TemplateQuestionCategory) {
        return (
          <button onClick={this.handleNewQuestion(category)} type="button">
            New{" "}
            {TemplateQuestionCategory[category].charAt(0).toUpperCase() +
              TemplateQuestionCategory[category].slice(1)}{" "}
            Question
          </button>
        );
      }

      private renderQuestion = (question: ITemplateQuestionEdit) => {
        const { classes } = this.props;
        if (question.deleted) {
          return (
            <Grid container={true}>
              <Grid
                container={true}
                justifyContent="flex-start"
                alignItems="center"
                spacing={2}
              >
                <Grid item={true}>
                  <this.DragHandle />
                </Grid>
                <Grid item={true}>
                  <button
                
                    onClick={this.handleQuestionDeleteToggle(question)}
                    type="button"
                  >
                    Restore
                  </button>
                </Grid>
              </Grid>
            </Grid>
          );
        }
        return (
          <div >
          <Grid container={true}>
            <Grid
              container={true}
              justifyContent="flex-start"
              alignItems="center"
              spacing={2}
            >
              <Grid item={true}>
                <this.DragHandle />
              </Grid>
              <Grid item={true}>
                <select
              
                  onChange={this.handleQuestionTypeChange(question)}
                  defaultValue={question.type.toString()}
                >
                  {[
                    TemplateQuestionType.scale,
                    TemplateQuestionType.written,
                  ].map((s: TemplateQuestionType) => (
                    <option key={s} value={s}>
                      {TemplateQuestionUtil.typeToString(s)}
                    </option>
                  ))}
                </select>
              </Grid>
              <Grid item={true}>
                <button
              
                  onClick={this.handleQuestionDeleteToggle(question)}
                  type="button"
                >
                  Delete
                </button>
              </Grid>
                         <Grid item={true}>
                <button
              
                  onClick={this.handleQuestionLockToggle(question)}
                  type="button"
                >
                  {question.status ? "Unlock question" : "Lock question"}
                </button>
              </Grid>
            </Grid>
            <Grid container={true} justifyContent="flex-start" spacing={1}>
             
                <Grid item={true} xs={3}>
                <textarea
                
                  rows={3}
                  onChange={this.handleQuestionChangeTextArea(
                    question,
                    "studentText"
                  )}
                  defaultValue={question.studentText}
                  className={classes.textArea}
                />
              </Grid>
              <Grid item={true} xs={3}>
                <textarea
                 
                  rows={3}
                  onChange={this.handleQuestionChangeTextArea(
                    question,
                    "coachText"
                  )}
                  defaultValue={question.coachText}
                  className={classes.textArea}
                />
              </Grid>
             
              <Grid item={true} xs={3}>
                <textarea
            
                  rows={3}
                  onChange={this.handleQuestionChangeTextArea(
                    question,
                    "parentText"
                  )}
                  defaultValue={question.parentText}
                  className={classes.textArea}
                />
              </Grid>
             
             
            </Grid>
            {(() => {
              if (TemplateQuestionType.scale === question.type) {
                const {
                  defaultNegativeText,
                  defaultPositiveText,
                  defaultReversed,
                  defaultScale,
                } = this.state;
                const notDefault = !(
                  question.scaleNegativeText === defaultNegativeText &&
                  question.scalePositiveText === defaultPositiveText &&
                  question.scaleReversed === defaultReversed &&
                  question.scaleScale === defaultScale
                );

                const preview = (
                  <Grid container={true} direction="row" alignItems="center">
                    <Box className={classes.radioPreview}>
                      <RadioScale
                        scale={question.scaleScale}
                        onChange={this.handleScalePreviewChange}
                        reversed={question.scaleReversed}
                        value={""}
                        positiveText={question.scalePositiveText}
                        negativeText={question.scaleNegativeText}
                      />
                    </Box>
                  </Grid>
                );
                const scalePropertyClass = notDefault
                  ? classes.scaleProperties
                  : classes.scalePropertiesDefault;
                if (this.state.showScaleOptions) {
                  return (
                    <Box
                      className={`${scalePropertyClass} ${classes.scalePropertyDiv}`}
                    >
                      <button
                   
                        onClick={this.toggleShowScaleOptions()}
                        type="button"
                      >
                        hide
                      </button>
                      <Box >
                        <label>
                        Scale:
                        </label>
                        <br/>
                        <select
                          onChange={this.handleQuestionChangeSelectElement(
                            question,
                            "scaleScale"
                          )}
                          value={question.scaleScale.toString()}
                        >
                          {range(1, 11).map((i) => (
                            <option key={i} value={i}>
                              {i}
                            </option>
                          ))}
                        </select>
                      </Box>
                      <Box >
                        <label>
                        Reversed:
                        </label>
                        <input
                          checked={question.scaleReversed}
                          onChange={this.handleQuestionChangeInputElement(
                            question,
                            "scaleReversed"
                          )}
                          type="checkbox"
                        />
                      </Box>
                      <div className="d-flex">
                      <div >
                      <label>
                      Positive Text:
                      </label>
                      <textarea
                        rows={2}
                        value={question.scalePositiveText}
                        onChange={this.handleQuestionChangeTextArea(
                          question,
                          "scalePositiveText"
                        )}
                      />
                      </div>
                      <div >
                      <label>
                      Negative Text:
                      </label>
                      
                      <textarea
                        rows={2}
                        value={question.scaleNegativeText}
                        onChange={this.handleQuestionChangeTextArea(
                          question,
                          "scaleNegativeText"
                        )}
                      />{" "}
                      </div>
                      </div>
                      
                      {notDefault ? (
                        <button  onClick={this.setToDefault(question)}>
                          set to default
                        </button>
                      ) : null}
                      {preview}
                    </Box>
                  );
                }
                return (
                  <Box
                    className={`${scalePropertyClass} ${classes.scalePropertyDiv}`}
                  >
                    <button
                   
                      onClick={this.toggleShowScaleOptions()}
                      type="button"
                    >
                      show
                    </button>
                    {preview}
                  </Box>
                );
              }
              return null;
            })()}
          </Grid>
          </div>
        );
      };

      private renderInheritQuestion = (
        question: ITemplateQuestion,
        isParentSection: boolean
      ) => {
        const { classes } = this.props;
        return (
          <Box className={classes.questionRow}>
            <Box margin="0.5rem">
              {question.type === TemplateQuestionType.scale && (
                <RadioButtonChecked />
              )}
              {question.type === TemplateQuestionType.written && <Create />}
            </Box>
            <Box
              display="grid"
              gridTemplateColumns={isParentSection ? "1fr" : "1fr 1fr"}
              gridGap="1rem"
              width="100%"
            >
              {isParentSection && (
                <Typography
                  className={classNames([
                    classes.question,
                    classes.parentQuestion,
                  ])}
                >
                  {question.parentText}
                </Typography>
              )}
              {!isParentSection && (
                <>
                  <Typography
                    className={classNames([
                      classes.question,
                      classes.studentQuestion,
                    ])}
                  >
                    {question.studentText}
                  </Typography>
                  <Typography
                    className={classNames([
                      classes.question,
                      classes.coachQuestion,
                    ])}
                  >
                    {question.coachText}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        );
      };

      private getQuestionCategory(
        category: TemplateQuestionCategory,
        questions = this.state.questions
      ) {
        return questions.filter((q) => q.category === category);
      }

      private canSubmit = () => {
        const { submitting, name, loading } = this.state;
        return name !== "" && submitting === undefined && loading !== true;
      };
    }
  )
);



// WEBPACK FOOTER //
// ./src/pages/admin/template-editor/TemplateEditorPage.tsx